import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Chip,
  CircularProgress,
  Grid,
  Typography,
} from '@mui/material';
import './ItemCard.css';
import './ItemList.css';
import { getProductDeliveryCaption } from '../utils/productDelivery';
import CardTitleRow from './CardTitleRow';
import LoadErrorScreen from './LoadErrorScreen';

const TYPE_FILTERS = [
  { value: 'all', label: 'Все' },
  { value: 'item', label: 'Аренда' },
  { value: 'product', label: 'Продажа' },
  { value: 'service', label: 'Услуги' },
  { value: 'tour', label: 'Туры' },
];

const TYPE_LABELS = {
  item: 'Аренда',
  product: 'Продажа',
  service: 'Услуга',
  tour: 'Тур',
};

const TYPE_COLORS = {
  item: '#F9E79F',
  product: '#2e7d32',
  service: '#64b5f6',
  tour: '#ff9800',
};

const PAGE_SIZE = 24;

const getPhotoUrl = (photo) => {
  if (!photo) return null;
  if (typeof photo === 'string') {
    const trimmed = photo.trim();
    if (!trimmed) return null;
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
    if (trimmed.startsWith('/')) return `${process.env.REACT_APP_SERVER_URL}${trimmed}`;
    return `${process.env.REACT_APP_SERVER_URL}/${trimmed}`;
  }
  return null;
};

const getFirstPhoto = (entity) => {
  const photos = entity?.photos;
  if (!photos || !Array.isArray(photos)) return null;
  for (const p of photos) {
    const url = getPhotoUrl(p);
    if (url) return url;
  }
  return null;
};

const getResultLink = (result) => {
  const { type, data } = result;
  if (type === 'item') return `/item/${data.inventoryNumber || data._id}`;
  if (type === 'product') return `/products/${data.productNumber}`;
  if (type === 'service') return `/services/${data.serviceId || data._id}`;
  if (type === 'tour') return `/tours/${data.tourNumber}`;
  return '/';
};

const getResultTitle = (result) => {
  const { type, data } = result;
  if (type === 'service') return data.name || 'Услуга';
  return data.title || 'Без названия';
};

const getResultPrice = (result) => {
  const { type, data } = result;
  if (type === 'item') return data.price != null ? `${data.price} ₽/сутки` : null;
  if (type === 'product') return data.price != null ? `${data.price} ₽` : 'Цена по запросу';
  if (type === 'service') return data.minPrice != null ? `от ${data.minPrice} ₽` : null;
  if (type === 'tour') return data.price != null ? `${data.price} ₽` : 'Цена по запросу';
  return null;
};

const SearchPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const query = (searchParams.get('q') || searchParams.get('search') || '').trim();
  const typeFilter = searchParams.get('type') || 'all';
  const branchId = searchParams.get('branchId') || '';

  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [facets, setFacets] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [error, setError] = useState('');
  const [branchAddressByKey, setBranchAddressByKey] = useState({});
  const loadMoreRef = useRef(null);

  const fetchResults = useCallback(
    async (offset = 0, append = false) => {
      if (!query) {
        setResults([]);
        setTotal(0);
        setFacets({});
        return;
      }
      if (append) setLoadingMore(true);
      else {
        setLoading(true);
        setLoadError(false);
      }
      setError('');
      try {
        const params = new URLSearchParams();
        params.set('q', query);
        params.set('limit', String(PAGE_SIZE));
        params.set('offset', String(offset));
        if (typeFilter && typeFilter !== 'all') params.set('types', typeFilter);
        if (branchId) params.set('branchId', branchId);

        const res = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/api/search?${params.toString()}`
        );
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.message || 'Ошибка поиска');

        setTotal(data.total || 0);
        setFacets(data.facets?.types || {});
        setResults((prev) => (append ? [...prev, ...(data.results || [])] : data.results || []));
      } catch (e) {
        if (!append) setLoadError(true);
        setError(e.message || 'Ошибка поиска');
        if (!append) {
          setResults([]);
          setTotal(0);
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [query, typeFilter, branchId]
  );

  useEffect(() => {
    fetchResults(0, false);
  }, [fetchResults]);

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/admin/branches/public`);
        if (!res.ok) return;
        const list = await res.json();
        const map = {};
        (list || []).forEach((b) => {
          const key = b.branchId || b._id;
          if (key) map[key] = [b.city, b.address].filter(Boolean).join(', ') || key;
        });
        setBranchAddressByKey(map);
      } catch (e) {
        console.error('Ошибка загрузки филиалов:', e);
      }
    };
    fetchBranches();
  }, []);

  useEffect(() => {
    const sentinel = loadMoreRef.current;
    if (!sentinel || results.length >= total || loading || loadingMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && results.length < total) {
          fetchResults(results.length, true);
        }
      },
      { rootMargin: '300px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [results.length, total, loading, loadingMore, fetchResults]);

  const setTypeFilter = (value) => {
    const params = new URLSearchParams(location.search);
    if (value === 'all') params.delete('type');
    else params.set('type', value);
    navigate(`/search?${params.toString()}`, { replace: true });
  };

  if (!query) {
    return (
      <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Поиск по каталогу
        </Typography>
        <Typography color="text.secondary">
          Введите запрос в строку поиска — найдём аренду, товары, услуги и туры.
        </Typography>
      </Box>
    );
  }

  if (loadError && !loading) {
    return <LoadErrorScreen />;
  }

  return (
    <Box sx={{ px: { xs: 0.5, md: 2 }, py: 2, maxWidth: 1400, mx: 'auto' }}>
      <Typography variant="h6" sx={{ px: 1, mb: 1, fontWeight: 600 }}>
        Результаты по запросу «{query}»
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ px: 1, mb: 2 }}>
        {loading ? 'Ищем…' : `Найдено: ${total}`}
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, px: 1, mb: 2 }}>
        {TYPE_FILTERS.map((f) => {
          const count =
            f.value === 'all'
              ? total
              : facets[f.value] ?? 0;
          return (
            <Chip
              key={f.value}
              label={`${f.label}${count ? ` (${count})` : ''}`}
              onClick={() => setTypeFilter(f.value)}
              color={typeFilter === f.value ? 'primary' : 'default'}
              variant={typeFilter === f.value ? 'filled' : 'outlined'}
              sx={{ backgroundColor: typeFilter === f.value ? '#8B7D6B' : undefined }}
            />
          );
        })}
      </Box>

      {error && (
        <Typography color="error" sx={{ px: 1, mb: 2 }}>
          {error}
        </Typography>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress sx={{ color: '#8B7D6B' }} />
        </Box>
      ) : results.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Ничего не найдено
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Попробуйте изменить запрос или выбрать другую категорию.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={1} className="items-grid-container" sx={{ px: 0.5 }}>
          {results.map((result) => {
            const photoUrl = getFirstPhoto(result.data) || 'https://via.placeholder.com/300';
            const title = getResultTitle(result);
            const price = getResultPrice(result);
            const link = getResultLink(result);
            const label = TYPE_LABELS[result.type] || result.typeLabel;
            const labelColor = TYPE_COLORS[result.type] || '#ccc';
            const productDeliveryCaption =
              result.type === 'product'
                ? getProductDeliveryCaption(result.data, branchAddressByKey)
                : null;

            return (
              <Grid item xs={6} sm={6} md={4} lg={3} key={`${result.type}-${result.data._id}`}>
                <Box
                  component={Link}
                  to={link}
                  className="card-container"
                  sx={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
                >
                  <Box sx={{ position: 'relative' }}>
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 8,
                        left: 8,
                        zIndex: 1,
                        backgroundColor: labelColor,
                        color: result.type === 'product' ? '#fff' : '#000',
                        px: 1,
                        py: 0.25,
                        borderRadius: '8px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                      }}
                    >
                      {label}
                    </Box>
                    <div className="slider-image-container">
                      <img
                        src={photoUrl}
                        alt={title}
                        className="card-image"
                        loading="lazy"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/300';
                        }}
                      />
                    </div>
                  </Box>
                  <Box className="card-content" sx={{ p: 1.5 }}>
                    <CardTitleRow type={result.type} entityId={result.data._id}>
                      {title}
                    </CardTitleRow>
                    {price && (
                      <Typography
                        className="card-price"
                        sx={{ fontWeight: 'bold', color: '#2e7d32', fontSize: '0.85rem', mt: 0.5 }}
                      >
                        {price}
                      </Typography>
                    )}
                    {result.type === 'item' && result.data.branchId && branchAddressByKey[result.data.branchId] && (
                      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
                        📍 {branchAddressByKey[result.data.branchId]}
                      </Typography>
                    )}
                    {productDeliveryCaption && (
                      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.7rem', mt: 0.5 }}>
                        {productDeliveryCaption}
                      </Typography>
                    )}
                    {result.type === 'service' && result.data.city && (
                      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
                        📍 {result.data.city}
                      </Typography>
                    )}
                    {result.type === 'tour' && result.data.destination && (
                      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
                        ✈ {result.data.destination}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      )}

      {loadingMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} sx={{ color: '#8B7D6B' }} />
        </Box>
      )}
      <Box ref={loadMoreRef} sx={{ height: 1 }} />
    </Box>
  );
};

export default SearchPage;
